import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';
import { SessionStorageService } from '../../services/session-storage.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { IService } from '../../services/client.service';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/services/app.service';

@Component({
    selector: 'app-overview',
    templateUrl: './overview.component.html',
    styleUrl: './overview.component.scss',
    standalone: true,
    imports: [CommonModule, CurrencyPipe, MatButtonModule, MatIconModule]
})
export class OverviewComponent implements OnInit, OnDestroy {

  dealership: any;
  vehicleInfo: any;
  appointment: any;
  customerInfo: any;
  selectedServices: IService[] = [];
  isConfirmationPage = false;
  isLoading = false;
  private readonly sub = new Subscription();

  constructor(
    private readonly sessionStorageService: SessionStorageService,
    private readonly router: Router, private readonly appService: AppService
  ) { }

  ngOnInit(): void {
    this.getSessionStorageData();
    this.isConfirmationPage = this.router.url.includes('confirmation');
    this.sub.add(
      this.appService.loader$.subscribe(value => {
        this.isLoading = value;
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private getHour12Preference(): boolean | null {
    const locale = this.getCurrentLocale();
    try {
      const resolved = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions();
      if (resolved.hourCycle === 'h11' || resolved.hourCycle === 'h12') return true;
      if (resolved.hourCycle === 'h23' || resolved.hourCycle === 'h24') return false;
    } catch {}
    try {
      const probe = new Date();
      const out = probe.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
      return /13/.test(out) ? false : true;
    } catch {}
    return null;
  }

  private getCurrentLocale(): string {
    try {
      const stored = window.localStorage.getItem('lang');
      const candidate = stored || navigator?.language || 'en-US';
      try {
        new Intl.DateTimeFormat(candidate);
        return candidate;
      } catch {
        return 'en-US'; 
      }
    } catch {
      return 'en-US';
    }
  }

  getSessionStorageData(){
    const sessionDataString = this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString ? JSON.parse(sessionDataString) : null;

    if (sessionData) {
      this.dealership = sessionData.dealership;
      this.vehicleInfo = sessionData.vehicleInfo;
      this.appointment = sessionData.appointment;
      this.customerInfo = sessionData.customerInfo;
      this.selectedServices = sessionData.appointment?.services || [];
    }
  }

  getServicePrice(service: IService): string {
    if (!service.unitPrice) {
      return $localize`Calculated at service center`;
    }
    
    const price = service.unitPrice.grossPrice || service.unitPrice.netPrice;
    const currency = service.unitPrice.currency;
    const vatLabel = service.unitPrice.grossPrice ? $localize`incl. VAT` : $localize`excl. VAT`;
    
    return `${currency}${price} ${vatLabel}`;
  }

  getFormattedDate(): string {
    const date = this.appointment?.appointmentDate;
    
    if (date) {
      const appointmentDate = new Date(date);
      const locale = this.getCurrentLocale();
      return appointmentDate.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      });
    }
    
    return $localize`Not Provided By User`;
  }

  getFormattedTime(): string {
    const timeslot = this.appointment?.selectedTimeslot;
    
    if (timeslot) {
      const parts = timeslot.split(' - ');
      const start = parts[0] || timeslot;
      const reformatted = this.reformatTimeslotString(start);
      return reformatted || start;
    }
    
    return $localize`Not Provided By User`;
  }

  private parseTimeslotToDate(timeslot: string): Date | null {
    if (!timeslot) return null;
    const trimmed = timeslot.trim();
    const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1], 10);
      const minutes = parseInt(ampmMatch[2], 10);
      const period = ampmMatch[3].toUpperCase();
      if (period === 'PM' && hours < 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      const d = new Date();
      d.setHours(hours, minutes, 0, 0);
      return d;
    }
    const h24Match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (h24Match) {
      const hours = parseInt(h24Match[1], 10);
      const minutes = parseInt(h24Match[2], 10);
      const d = new Date();
      d.setHours(hours, minutes, 0, 0);
      return d;
    }
    return null;
  }

  private reformatTimeslotString(timeslot: string): string {
    const d = this.parseTimeslotToDate(timeslot);
    if (!d) return timeslot;
    const locale = this.getCurrentLocale();
    const hour12Pref = this.getHour12Preference();
    try {
      return d.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        ...(hour12Pref === null ? {} : { hour12: hour12Pref })
      } as Intl.DateTimeFormatOptions);
    } catch {
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
  }

  getFormattedDateTime(): string {
    const date = this.getFormattedDate();
    const time = this.getFormattedTime();
    
    if (date !== $localize`Not Provided By User` && time !== $localize`Not Provided By User`) {
      return `${date}\n${time}`;
    }
    
    if (date !== $localize`Not Provided By User`) {
      return date;
    }
    
    return $localize`Not Provided By User`;
  }

  getCustomerName(): string {
    return this.customerInfo?.name;
  }

  getCustomerPhone(): string {
    return this.customerInfo?.phoneNo;
  }

  getCustomerEmail(): string {
    return this.customerInfo?.email;
  }

  getVehicleModel(): string {
    return `${this.vehicleInfo?.make || $localize`Not Provided By User`}`;
  }

  getVehicleVin(): string {
    return this.vehicleInfo?.vin || $localize`VIN not provided by User`;
  }

  getVehicleLicense(): string {
    return this.vehicleInfo?.licenseNo;
  }

  getServiceCenterName(): string {
    return this.dealership?.Name;
  }

  getServiceCenterAddress(): string {
    return this.dealership?.Address;
  }

  getAdvisorName(): string {
    return this.appointment?.serviceAdvisorName || $localize`Not Provided By User`;
  }

  onChangeCustomer(): void {
    this.router.navigate(['/book-appointment/customer-info'], { 
      state: { fromOverviewChange: true } 
    });
  }

  goToConfirmation() {
    this.router.navigate(['/confirmation']);
  }

  close() {
    if (this.isConfirmationPage) {
      this.sessionStorageService.setSessionItem('showMapView', 'false');
      this.sessionStorageService.removeSessionItem('oabAppSessionData');
    }
    this.router.navigate(['/locations']);
  }
}
